import { useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Recorder({ onTranscript, disabled }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = handleStop;
      mr.start();
      mediaRef.current = mr;
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone access denied. Please allow it in your browser.");
    }
  };

  const stop = () => {
    if (mediaRef.current && state === "recording") {
      mediaRef.current.stop();
      clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    }
  };

  const handleStop = async () => {
    setState("transcribing");
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size === 0) {
      setState("idle");
      return;
    }
    const fd = new FormData();
    fd.append("audio", blob, "recording.webm");
    try {
      const { data } = await api.post("/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.text?.trim()) {
        onTranscript(data.text.trim());
        toast.success("Got it! Your words were added below.");
      } else {
        toast.info("Didn't catch that. Try speaking again.");
      }
    } catch (e) {
      toast.error("Couldn't transcribe. Please try again.");
    } finally {
      setState("idle");
    }
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(
    seconds % 60
  ).padStart(2, "0")}`;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative flex items-center justify-center">
        {state === "recording" && (
          <>
            <span className="absolute w-24 h-24 rounded-full bg-[#C26D5C]/40 pulse-ring" />
            <span className="absolute w-24 h-24 rounded-full bg-[#C26D5C]/30 pulse-ring" style={{ animationDelay: "0.5s" }} />
          </>
        )}
        <button
          data-testid={state === "recording" ? "stop-dictation-btn" : "start-dictation-btn"}
          onClick={state === "recording" ? stop : start}
          disabled={disabled || state === "transcribing"}
          className={`relative z-10 w-24 h-24 rounded-full flex items-center justify-center border transition-transform hover:-translate-y-1 disabled:opacity-60 disabled:hover:translate-y-0 ${
            state === "recording"
              ? "bg-[#C26D5C] border-[#a9553f] shadow-[0_10px_22px_rgba(194,109,92,0.4)]"
              : "bg-[#4A6B53] border-[#3D5A45] shadow-[0_10px_22px_rgba(74,107,83,0.35)]"
          }`}
        >
          {state === "transcribing" ? (
            <Loader2 className="w-9 h-9 text-white animate-spin" strokeWidth={1.5} />
          ) : state === "recording" ? (
            <Square className="w-8 h-8 text-white fill-white" strokeWidth={1.5} />
          ) : (
            <Mic className="w-10 h-10 text-white" strokeWidth={1.5} />
          )}
        </button>
      </div>
      <div className="text-center h-6">
        {state === "idle" && (
          <span className="text-sm font-medium text-[#4A6B53]">Tap & talk</span>
        )}
        {state === "recording" && (
          <span className="text-sm font-semibold text-[#C26D5C] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#C26D5C] animate-pulse" />
            Listening… {mmss} — tap to stop
          </span>
        )}
        {state === "transcribing" && (
          <span className="text-sm font-medium text-[#D4A373]">Writing down your words…</span>
        )}
      </div>
    </div>
  );
}
